/**
 * Door Planner product rules, colour table and copy.
 *
 * `ops init` never rewrites this file — it is the planner-specific vertical
 * (issue #14), not slug/region/estate identity. Manufactured size bands and
 * prices stay on the catalogue feed (`erp/feeds/products.ts`); they are not
 * hard-coded here.
 *
 * Rules reflect the confirmed Door Planner specification. Colour `name`
 * values are the live GI `frameColour` strings.
 */
export const doorTypeIds = ['barrier', 'security'];
export const measurementGuides = {
    diy: {
        publicId: 'doors/guides/measuring-guide',
        filename: 'ProtectorAl_Security_Door_Measuring_Guide.pdf',
    },
    trade: {
        publicId: 'doors/guides/exact-door-size-guide',
        filename: 'ProtectorAl_Enter_Exact_Door_Size.pdf',
    },
};
export const doorRules = {
    /** mm deducted from the opening in DIY (measure-it-yourself) mode. */
    clearance: { width: 5, height: 10 },
    /** Width spread at or above this needs an out-of-square acknowledgement. */
    outOfSquareToleranceMm: 5,
    /** Height spread above this needs a separate acknowledgement. */
    heightSpreadToleranceMm: 10,
    /** Existing-handle clearance (B) in millimetres. */
    depthClearance: { min: 80, recommended: 100 },
    /** Handle-centre height band from the bottom of the manufactured door. */
    lockHeight: { min: 1050, max: 1300 },
    /** Triple-lock rod geometry, centre-to-centre (mm). */
    securityLockOffsets: { top: 520, bottom: 855 },
    /** Minimum bolt-to-door-edge clearance applied to every bolt. */
    lockEdgeClearanceMm: 150,
    /** Guidance only; customers must enter their own handle height. */
    recommendedLockHeightMm: 1200,
    /** Always three hinges: ends + mid-height. */
    hinges: { count: 3, edgeOffsetMm: 180 },
    doorNameMaxLength: 80,
    quantity: { min: 1, max: 20 },
    maxDoorsPerOrder: 50,
    designVersion: 1,
};
/** Base handle-height band. Security clearance is checked separately. */
export function lockHeightRange(_doorType, _doorHeightMm) {
    return { ...doorRules.lockHeight };
}
/** Hinge centre heights from the bottom of the door. */
export function hingePositions(doorHeightMm) {
    const offset = doorRules.hinges.edgeOffsetMm;
    return [offset, doorHeightMm / 2, doorHeightMm - offset];
}
export const doorColours = [
    { name: 'Primrose', id: 'pg', hex: '#e9e4c9', renderSlug: 'primrose' },
    { name: 'Monument', id: 'ms', hex: '#3a3c3f', renderSlug: 'monument' },
    { name: 'Pale Eucalypt', id: 'pes', hex: '#7c8e7a', renderSlug: 'pale-eucalypt' },
    { name: 'Palladium Silver', id: 'ps', hex: '#b4b7b9', renderSlug: 'palladium-silver' },
    { name: 'Woodland Grey', id: 'wgs', hex: '#4d4f4b', renderSlug: 'woodland-grey' },
    { name: 'Pearl White', id: 'pwg', hex: '#f3f2ec', renderSlug: 'pearl-white' },
    { name: 'Black', id: 'sb', hex: '#1c1c1c', renderSlug: 'black' },
    { name: 'Paperbark', id: 'pbs', hex: '#cabfa4', renderSlug: 'paperbark' },
    { name: 'Surfmist', id: 'ss', hex: '#e4e2d5', renderSlug: 'surfmist' },
    { name: 'Deep Ocean', id: 'dos', hex: '#36455e', renderSlug: 'deep-ocean' },
];
/** Colour shown in the indicative preview before the customer chooses a colour. */
export const doorPreviewColour = 'Black';
export const doorHardwareColour = 'Satin Black';
/** Live GI `frameColour` names, derived from the colour table. */
export const frameColourNames = doorColours.map((colour) => colour.name);
/** Look up a colour by the GI `frameColour` value. Throws on unknown names. */
export function colourByName(name) {
    const colour = doorColours.find((candidate) => candidate.name === name.trim());
    if (!colour) {
        const known = doorColours.map((candidate) => candidate.name).join(', ');
        throw new Error(`Unknown frame colour "${name}". Known colours: ${known}`);
    }
    return colour;
}
export const doorCopy = {
    barrier: {
        title: 'Barrier Screen Door',
        subtitle: 'Ventilation, insect protection and simple access control',
        features: [
            'Aluminium Diamond Grille',
            'Fibreglass Insect Mesh',
            'Single Lock (Key lockable)',
            '10 Year Warranty',
        ],
        included: [
            'Pre-assembled door',
            'Three heavy-duty hinges (180mm from each end to the hinge centre, one at mid-height)',
            'Key-lockable single lock with handle - 62mm 5-pin Euro cylinder with 2 keys',
        ],
        meshLabel: 'Fibreglass Insect Mesh',
        infillLabel: 'Aluminium Diamond Grille',
    },
    security: {
        title: 'Security Screen Door',
        subtitle: 'Maximum protection with clear views',
        features: [
            '316 Marine Grade Woven Stainless Steel Mesh',
            'Triple Lock system (Key lockable)',
            'Patented Friction Grip System',
            '10 Year Warranty',
        ],
        included: [
            'Pre-assembled door panel (Woven 316 Stainless Steel mesh)',
            'Three heavy-duty hinges (180mm from each end to the hinge centre, one at mid-height)',
            'Key-lockable triple-lock security system with handle - 62mm 5-pin Euro cylinder with 2 keys',
        ],
        meshLabel: '316 Marine Grade Woven Stainless Steel Mesh',
        infillLabel: 'Patented Friction Grip System',
    },
};
export const doorSharedCopy = {
    depthClearance: `At least ${doorRules.depthClearance.min} mm depth clearance is required; ${doorRules.depthClearance.recommended} mm or more is recommended.`,
    handing: 'Stand outside your home and face the door. All doors swing outwards towards you.',
    toolsList: [
        'Tape measure',
        'Pencil',
        'Spirit level',
        'Drill/driver with suitable drill bits',
        'Screwdriver or driver bits',
        'Hammer',
        'Chisel',
        'Packers or wedges',
        'Safety glasses and gloves',
    ],
    wallFixingsNote: "Wall fixings aren't included as the correct type depends on your wall type.",
    warranty: '10 Year Warranty',
    /**
     * Placeholders until #16 supplies the real measuring video, installation guide and legal
     * URLs. Empty string means "not published yet" — do not fall back to `#`.
     */
    urls: {
        measuringVideo: '',
        installationGuide: '',
        terms: '',
        privacy: '',
    },
};
export function getDoorCopy(type) {
    return doorCopy[type];
}
//# sourceMappingURL=doors.js.map